import { useEffect, useMemo, useState } from 'react';
import { VARIABLES_BY_KEY, type VariableKey } from '../lib/census-variables';
import { formatValue } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { openShare, trackShare } from '../lib/share';
import type { MunicipioRecord, PrWide } from '../types';
import { MunicipioPanel } from './MunicipioPanel';
import { VariableSelector } from './VariableSelector';
import { Legend, type LegendMode } from './Legend';

type Sort = 'value_desc' | 'value_asc' | 'name';

export function ListView({
  records,
  variable,
  onVariable,
  prWide,
  selected,
  onSelect,
  mode,
  onMode,
  extent,
  onExtent,
}: {
  records: MunicipioRecord[];
  variable: VariableKey;
  onVariable: (v: VariableKey) => void;
  prWide: PrWide;
  selected: string | null;
  onSelect: (id: string | null) => void;
  mode: LegendMode;
  onMode: (m: LegendMode) => void;
  extent: { min: number | null; max: number | null };
  onExtent: (min: number | null, max: number | null) => void;
}) {
  void extent;
  const { t, lang } = useI18n();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('value_desc');
  const noData = t('app.data.unavailable');
  const meta = VARIABLES_BY_KEY[variable];

  // Compute extent here so the parent Legend stays accurate when the list
  // view is active (the Map component normally reports extent, but it
  // doesn't render in list mode).
  const { min, max } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const r of records) {
      const v = (r as unknown as Record<string, number | null>)[variable];
      if (v == null || !Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    if (!Number.isFinite(lo)) return { min: null as number | null, max: null as number | null };
    return { min: lo, max: hi };
  }, [records, variable]);

  useEffect(() => {
    onExtent(min, max);
  }, [min, max, onExtent]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = records
      .map((r) => ({
        rec: r,
        v: (r as unknown as Record<string, number | null>)[variable],
      }))
      .filter(({ rec }) => (q ? rec.name.toLowerCase().includes(q) : true));

    switch (sort) {
      case 'value_desc':
        rows.sort((a, b) => (b.v ?? -Infinity) - (a.v ?? -Infinity));
        break;
      case 'value_asc':
        rows.sort((a, b) => (a.v ?? Infinity) - (b.v ?? Infinity));
        break;
      case 'name':
        rows.sort((a, b) => a.rec.name.localeCompare(b.rec.name, lang === 'es' ? 'es' : 'en'));
        break;
    }
    return rows;
  }, [records, query, sort, variable, lang]);

  const rank = useMemo(() => {
    const m = new Map<string, number>();
    const ranked = [...records]
      .map((r) => ({
        id: r.GEOID,
        v: (r as unknown as Record<string, number | null>)[variable],
      }))
      .filter((x) => x.v != null)
      .sort((a, b) => (b.v ?? 0) - (a.v ?? 0));
    ranked.forEach((x, i) => m.set(x.id, i + 1));
    return { map: m, total: ranked.length };
  }, [records, variable]);

  const selectedRec = selected ? records.find((r) => r.GEOID === selected) ?? null : null;
  const barMax = Math.max(Math.abs(min ?? 0), Math.abs(max ?? 1));

  const [controlsOpen, setControlsOpen] = useState(false);
  const currentLabel = t(`var.${variable}.label`);

  return (
    <div className="flex w-full flex-col bg-an-paper">
      {/* Compact always-visible indicator bar. On mobile this is the
          only piece that stays on screen when you scroll; tap "Cambiar"
          to expand the full variable picker. On desktop the full
          picker + legend are visible below without needing to expand. */}
      <div className="sticky top-0 z-20 border-b border-an-rule bg-an-white">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-3 py-2 md:hidden">
          <div className="min-w-0 flex-1">
            <div className="font-body text-[10px] uppercase tracking-widest text-an-muted">
              {t('app.selector.title')}
            </div>
            <div className="display truncate text-[13px] uppercase tracking-tight">
              {currentLabel}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setControlsOpen((v) => !v)}
            aria-expanded={controlsOpen}
            className="display shrink-0 border border-an-rule px-2.5 py-1.5 text-[10px] uppercase tracking-widest hover:bg-an-black hover:text-an-white"
          >
            {controlsOpen ? '×' : t('app.list.changeIndicator')}
          </button>
        </div>

        {/* Mobile expanded drawer — holds the full VariableSelector +
            Legend. Closed by default so the list gets the viewport. */}
        {controlsOpen && (
          <div className="border-t border-an-rule bg-an-white md:hidden">
            <div className="mx-auto max-w-3xl p-4">
              <VariableSelector
                value={variable}
                onChange={(v) => {
                  onVariable(v);
                  setControlsOpen(false);
                }}
              />
              <div className="mt-4">
                <Legend
                  variable={variable}
                  mode={mode}
                  onMode={onMode}
                  min={min}
                  max={max}
                />
              </div>
            </div>
          </div>
        )}

        {/* Search + sort row. Always visible on every breakpoint — it's
            the smallest surface that still lets the user reshape the
            list, and it fits on one line on mobile. */}
        <div className="border-t border-an-rule bg-an-paper">
          <div className="mx-auto flex max-w-3xl items-center gap-2 p-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('app.list.search')}
              className="min-w-0 flex-1 border border-an-rule bg-an-white px-3 py-2 font-body text-sm placeholder:text-an-muted focus:outline-none focus:ring-1 focus:ring-an-black"
              aria-label={t('app.list.search')}
            />
            <div className="flex shrink-0 items-center gap-1">
              {(['value_desc', 'value_asc', 'name'] as Sort[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSort(s)}
                  aria-label={t(
                    `app.list.sort.${s === 'value_desc' ? 'valueDesc' : s === 'value_asc' ? 'valueAsc' : 'name'}`,
                  )}
                  title={t(
                    `app.list.sort.${s === 'value_desc' ? 'valueDesc' : s === 'value_asc' ? 'valueAsc' : 'name'}`,
                  )}
                  className={[
                    'display border px-2 py-1 text-[10px] uppercase tracking-widest',
                    sort === s
                      ? 'border-an-black bg-an-black text-an-white'
                      : 'border-an-rule bg-an-white text-an-ink',
                  ].join(' ')}
                >
                  {s === 'value_desc' ? '↓' : s === 'value_asc' ? '↑' : 'A–Z'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop-only full controls panel. Not shown on mobile — there
          the drawer above covers it. */}
      <div className="hidden border-b border-an-rule bg-an-white md:block">
        <div className="mx-auto max-w-3xl p-4">
          <VariableSelector value={variable} onChange={onVariable} />
          <div className="mt-4">
            <Legend
              variable={variable}
              mode={mode}
              onMode={onMode}
              min={min}
              max={max}
            />
          </div>
        </div>
      </div>

      <ol className="mx-auto w-full max-w-3xl" aria-live="polite">
        {items.length === 0 && (
          <li className="p-6 text-center font-body text-sm text-an-muted">
            {t('app.list.empty')}
          </li>
        )}
        {items.map(({ rec, v }) => {
          const r = rank.map.get(rec.GEOID) ?? null;
          const formatted = formatValue(v ?? null, meta.format, lang, noData);
          const pct = v != null && barMax > 0 ? Math.min(1, Math.abs(v) / barMax) : 0;
          return (
            <li
              key={rec.GEOID}
              className="border-b border-an-rule/60 hover:bg-an-white"
            >
              <div className="flex items-stretch">
                <button
                  type="button"
                  onClick={() => onSelect(rec.GEOID)}
                  className="relative flex flex-1 flex-col gap-1 px-4 py-3 text-left"
                  aria-label={`${rec.name} — ${formatted}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="display text-base uppercase leading-tight tracking-tight md:text-lg">
                      {rec.name}
                    </span>
                    <span className="stat-num whitespace-nowrap text-xl md:text-2xl">
                      {formatted}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-body text-[11px] uppercase tracking-widest text-an-muted">
                      {r != null ? `#${r}/${rank.total}` : '—'}
                    </span>
                    <div className="relative h-[3px] flex-1 bg-an-rule/15">
                      <div
                        className="absolute inset-y-0 left-0 bg-an-black"
                        style={{ width: `${pct * 100}%` }}
                      />
                    </div>
                  </div>
                </button>
                <QuickShareButton rec={rec} variable={variable} formatted={formatted} />
              </div>
            </li>
          );
        })}
        {items.length > 0 && (
          <li className="p-4 text-center font-body text-[11px] text-an-muted">
            {t('app.list.showing', { count: items.length, total: records.length })}
          </li>
        )}
      </ol>

      {selectedRec && (
        <DetailOverlay
          rec={selectedRec}
          variable={variable}
          prWide={prWide}
          rank={rank.map.get(selectedRec.GEOID) ?? null}
          total={rank.total}
          onClose={() => onSelect(null)}
        />
      )}
    </div>
  );
}

function QuickShareButton({
  rec,
  variable,
  formatted,
}: {
  rec: MunicipioRecord;
  variable: VariableKey;
  formatted: string;
}) {
  const { t, lang } = useI18n();
  const label = t('app.list.shareQuick');

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    const input = {
      geoid: rec.GEOID,
      municipio: rec.name,
      variable,
      formattedValue: formatted,
      lang,
      t,
    };
    // Prefer native share sheet (best mobile UX); fall back to WhatsApp —
    // the PR audience's dominant channel per brief §5.1.11.
    const channel = typeof navigator !== 'undefined' && 'share' in navigator ? 'native' : 'whatsapp';
    trackShare(input, channel);
    try {
      await openShare(channel, input);
    } catch {
      /* user cancelled */
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label} ${rec.name}`}
      title={label}
      className="flex w-12 shrink-0 items-center justify-center border-l border-an-rule/60 bg-an-white text-an-ink hover:bg-an-black hover:text-an-white"
    >
      <ShareIcon />
    </button>
  );
}

function ShareIcon() {
  // iOS-style share glyph: square with an arrow exiting the top.
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 15V3" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

function DetailOverlay({
  rec,
  variable,
  prWide,
  rank,
  total,
  onClose,
}: {
  rec: MunicipioRecord;
  variable: VariableKey;
  prWide: PrWide;
  rank: number | null;
  total: number;
  onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex flex-col bg-an-paper"
    >
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-an-rule bg-an-white px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="display flex items-center gap-2 border border-an-rule bg-an-white px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-an-black hover:text-an-white"
        >
          ← {t('app.list.back')}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <MunicipioPanel
          record={rec}
          variable={variable}
          prWide={prWide}
          rank={rank}
          total={total}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
